import { CaretDown } from 'assets'
import { Modal } from 'components'
import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { Filter } from './Filter'
import { useAppSelector, useBindDispatch, useAnalytics } from 'hooks'
import { buildFilterGroups } from 'pages/village/helpers/buildFilters'
import { ClientFilterType, FilterType } from 'types/clientFiltersTypes'
import { constants } from 'helpers'
import { useTranslation } from 'react-i18next'
import { omit } from 'lodash'
import FilterCard from './FilterCard'
import { colors } from 'styles/colors'

const ClientFilter = ({
    activeTab,
    searchValue,
    resetSearchValue
}: ClientFilterType) => {
    const { t } = useTranslation()
    const { VILLAGE_PAGE_TABS } = constants
    const { selectedFilter } = useAppSelector((state) => state.filterSelection)
    const { filterSelectorHandler } = useBindDispatch()
    const { trackEvent } = useAnalytics()

    const isPotentialClientTab =
        activeTab.key === VILLAGE_PAGE_TABS.PREVIOUS_CLIENT_TAB

    const filterVisibleRange = isPotentialClientTab ? 3 : 4

    const allFilterGroups = useMemo(() => buildFilterGroups(t), [t])

    const filterGroups = allFilterGroups[activeTab.key] ?? []

    const allFilters = useMemo(
        () => filterGroups.flatMap((group) => group.filters),
        [filterGroups]
    )

    const filtersByName = useMemo(() => {
        return allFilters.reduce((map, filter) => {
            map.set(filter.filterName, filter)
            return map
        }, new Map<string, FilterType>())
    }, [allFilters])

    const mapRearrangementToTab = useCallback(
        (
            activeTab: number,
            topFilters: FilterType[],
            selectedFilterElement: FilterType
        ) => {
            const option = {
                [VILLAGE_PAGE_TABS.PREVIOUS_CLIENT_TAB]: [
                    ...topFilters.slice(0, -1),
                    omit(selectedFilterElement, ['caption'])
                ],
                [VILLAGE_PAGE_TABS.CURRENT_CLIENT_TAB]: [
                    omit(selectedFilterElement, ['caption']),
                    ...topFilters.slice(2)
                ]
            }

            return option[activeTab]
        },
        [activeTab.key, selectedFilter]
    )

    const rearrangeVisibleFilters = () => {
        const topFilters = allFilters.slice(0, filterVisibleRange)

        const selectedFilterElement = filtersByName.get(selectedFilter || '')

        const shouldAddSelected =
            selectedFilterElement && !topFilters.includes(selectedFilterElement)

        return shouldAddSelected && selectedFilterElement
            ? mapRearrangementToTab(
                  activeTab.key,
                  topFilters,
                  selectedFilterElement
              )
            : topFilters
    }

    const [visibleFilters, setVisibleFilters] = useState<FilterType[]>([])
    const [moreFilters, setMoreFilters] = useState(filterGroups)
    const [viewMoreFilters, setViewMoreFilters] = useState(false)

    useEffect(() => {
        setVisibleFilters(rearrangeVisibleFilters())
        setMoreFilters(filterGroups)
    }, [activeTab.key, selectedFilter, allFilters, filterGroups])

    useEffect(() => {
        if (searchValue?.search) {
            filterSelectorHandler({ selectedFilter: undefined })
        }
    }, [searchValue?.search])

    const trackFilterClick = useCallback(() => {
        trackEvent({
            category: isPotentialClientTab
                ? constants.ANALYTICS.VILLAGE_POTENTIAL
                : constants.ANALYTICS.VILLAGE_CLIENT,
            action: constants.ANALYTICS.ACTIONS.FILTER_LIST
        })
    }, [isPotentialClientTab, trackEvent])

    return (
        <div className="filters--container">
            {visibleFilters.map((filter) => (
                <Filter
                    key={filter.filterName}
                    filter={filter}
                    resetSearchValue={resetSearchValue}
                    onFilterSelect={trackFilterClick}
                />
            ))}
            <button
                onClick={() => setViewMoreFilters(true)}
                aria-label="open more filters"
                type="button"
                className="more-filters--open-modal-btn"
            >
                <input
                    type="radio"
                    name="filter"
                    id="more-option"
                    className="filters--input"
                />
                <label
                    htmlFor="more-option"
                    className="filters--item filters--more-item"
                >
                    <CaretDown />
                </label>
            </button>
            <Modal
                position="bottom"
                showModal={viewMoreFilters}
                showCloseIconBtn
                closeClickHandler={() => setViewMoreFilters(false)}
            >
                <div
                    className="more-filters--header"
                    style={{
                        background:
                            activeTab.key ===
                            VILLAGE_PAGE_TABS.PREVIOUS_CLIENT_TAB
                                ? colors.veryLightRed
                                : colors.veryLightGreen
                    }}
                >
                    <p className="more-filters--header-text">{t('filter')}</p>
                </div>
                <div>
                    {moreFilters.map((group) => (
                        <FilterCard
                            key={group.title}
                            filterTitle={group.title}
                            filterIcons={group.filters}
                            resetSearchValue={resetSearchValue}
                            viewMoreFilters={setViewMoreFilters}
                            onFilterSelect={trackFilterClick}
                        />
                    ))}
                </div>
            </Modal>
        </div>
    )
}

export default React.memo(ClientFilter)
